import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button, CopyableId, HelpTooltip, Input } from '@fiado/react-kit';
import { TechnicalDetails } from '../../../../components/shared/TechnicalDetails';
import { tenantApi } from '../../../../api/endpoints/tenant';
import { handleApiError } from '../../../../api/errorMapping';
import { ApiError } from '../../../../api/errors';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { Select } from '../../../../components/ui/select';
import type { UserFieldDefLocal } from '../../../../api/types/TenantLocal';

// La `key` es la llave inmutable del campo (clave en el map `customFields` y en el claim del token).
// Formato slug snake_case, mismo criterio que el backend para identificadores estables. Solo se
// escribe al CREAR; al editar es fija de por vida (se muestra readonly).
export const FIELD_KEY_REGEX = /^[a-z][a-z0-9_]{1,49}$/;

// Tipos de campo del catálogo. `select` exige al menos una opción no vacía; `text` no usa opciones.
const FIELD_TYPES = ['text', 'select'] as const;

// Esquema parametrizado por modo. Las reglas dependientes (key solo en alta, options solo en select)
// se afinan con `superRefine`. Se construye con `isEdit` porque al editar la `key` es inmutable
// (viene del backend) y NO se valida ni se envía. NOTA: la validación de la `key` vive aquí, NO en un
// `register('key', { validate })`: con `zodResolver` presente, los `validate` de `register` NO corren
// (el resolver reemplaza la validación nativa de RHF), así que el mensaje nunca aparecería.
const FIELD_KEY_ERROR =
    'Debe empezar con letra y usar solo minúsculas, números y guion bajo (mín. 2 caracteres).';

function buildSchema(isEdit: boolean) {
    return z
        .object({
            key: z.string().trim(),
            label: z.string().trim().min(1, 'Escribe una etiqueta para el campo.'),
            type: z.enum(FIELD_TYPES),
            // Opciones del select. Se filtran las vacías antes de validar/enviar.
            options: z.array(z.object({ value: z.string() })),
            required: z.boolean(),
            injectToToken: z.boolean(),
            // Solo se edita (al crear nace habilitado server-side). Ver mutationFn.
            enabled: z.boolean(),
        })
        .superRefine((v, ctx) => {
            // key: validada solo al CREAR (al editar es inmutable y no se manda).
            if (!isEdit && !FIELD_KEY_REGEX.test(v.key.trim())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: FIELD_KEY_ERROR,
                    path: ['key'],
                });
            }
            // options: un campo lista exige al menos una opción no vacía.
            if (v.type === 'select') {
                const clean = v.options.map((o) => o.value.trim()).filter(Boolean);
                if (clean.length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Un campo de tipo lista necesita al menos una opción.',
                        path: ['options'],
                    });
                }
            }
        });
}

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). El campo vive en ESTE tenant, no en el del actor. */
    tenantId: string;
    /** Si viene, el form edita ese campo (key + type fijos); si no, crea uno nuevo. */
    field?: UserFieldDefLocal;
    /** Se llama tras crear/editar OK (ej. cerrar el modal del padre). */
    onDone?: () => void;
}

/**
 * Alta/edición de un campo personalizado del catálogo del tenant (PLATFORM). El `tenantId` viene del
 * PATH (no del user logueado) y consume `tenantApi.createUserField`/`updateUserField` contra
 * `/tenants/{tid}/user-fields`. El backend re-enforza `platform.tenant.userfields.manage`.
 *
 * - CREAR: key (slug inmutable), label, type (text/select), options (si select), required,
 *   injectToToken. El campo nace habilitado server-side (`enabled` no se manda al crear).
 * - EDITAR: key y type son INMUTABLES (readonly); editable label, options (si select), required,
 *   injectToToken y enabled.
 */
export function UserFieldForm({ tenantId, field, onDone }: Props) {
    const isEdit = !!field;
    const qc = useQueryClient();
    const form = useForm<FormValues>({
        resolver: zodResolver(buildSchema(isEdit)),
        defaultValues: {
            key: field?.key ?? '',
            label: field?.label ?? '',
            type: field?.type ?? 'text',
            options: (field?.options ?? []).map((value) => ({ value })),
            required: field?.required ?? false,
            injectToToken: field?.injectToToken ?? false,
            enabled: field?.enabled ?? true,
        },
    });

    const type = useWatch({ control: form.control, name: 'type' });
    const options = useWatch({ control: form.control, name: 'options' });
    // checked de los checkboxes vía useWatch (re-render confiable) — NO `form.watch(...)` inline,
    // que actualiza el valor enviado pero no re-renderiza el `checked` visual (bug: el tilde no se
    // movía aunque el valor cambiara). Consistente con type/options de arriba.
    const requiredChecked = useWatch({ control: form.control, name: 'required' });
    const injectChecked = useWatch({ control: form.control, name: 'injectToToken' });
    const enabledChecked = useWatch({ control: form.control, name: 'enabled' });

    // `options` es un array: cuando el error es a nivel raíz (issue con path ['options']), RHF lo
    // guarda en `errors.options.root`, no en `errors.options.message`. Cubrimos ambos.
    const optionsErr = form.formState.errors.options;
    const optionsError = optionsErr?.message ?? optionsErr?.root?.message;

    const addOption = (): void => {
        form.setValue('options', [...form.getValues('options'), { value: '' }], {
            shouldValidate: false,
        });
    };
    const removeOption = (idx: number): void => {
        form.setValue(
            'options',
            form.getValues('options').filter((_, i) => i !== idx),
            { shouldValidate: true },
        );
    };

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'user-fields'] });

    const mut = useMutation({
        mutationFn: (v: FormValues) => {
            // Opciones limpias (sin vacíos), ordenadas alfabéticamente (default del proyecto). Solo
            // se mandan cuando el tipo es select.
            const cleanOptions =
                v.type === 'select'
                    ? [...new Set(v.options.map((o) => o.value.trim()).filter(Boolean))].sort(
                          (a, b) => a.localeCompare(b),
                      )
                    : undefined;

            if (isEdit) {
                // key y type son inmutables → no van en el body de update.
                return tenantApi.updateUserField(tenantId, field.key, {
                    label: v.label.trim(),
                    options: cleanOptions,
                    required: v.required,
                    injectToToken: v.injectToToken,
                    enabled: v.enabled,
                });
            }
            return tenantApi.createUserField(tenantId, {
                key: v.key.trim(),
                label: v.label.trim(),
                type: v.type,
                options: cleanOptions,
                required: v.required,
                injectToToken: v.injectToToken,
            });
        },
        onSuccess: async () => {
            toast.success(isEdit ? 'Campo actualizado' : 'Campo creado');
            await invalidate();
            if (onDone) onDone();
        },
        onError: (err) => {
            // Colisión de key al crear: el backend tira USER_FIELD_KEY_DUPLICATE. Lo mostramos en el
            // propio campo (que el usuario elija otra key) en vez de un toast genérico.
            if (err instanceof ApiError && err.code === 'USER_FIELD_KEY_DUPLICATE') {
                form.setError('key', {
                    message: `Ya existe un campo con la clave "${form.getValues('key').trim()}". Elige otra.`,
                });
                return;
            }
            // Resto de errores tipados del backend (USER_FIELD_OPTIONS_REQUIRED, etc.) → toast.
            handleApiError(err);
        },
    });

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            {/* Key: editable solo al crear; inmutable (readonly) al editar. */}
            {isEdit ? (
                <TechnicalDetails>
                    <CopyableId label="Clave (no editable)" value={field.key} />
                </TechnicalDetails>
            ) : (
                <Input
                    id="uff-key"
                    label="Clave"
                    hint="Identificador interno, en minúsculas: letras, números y guion bajo. Ej: documento_id. No se puede cambiar luego."
                    placeholder="documento_id"
                    error={form.formState.errors.key?.message}
                    {...form.register('key')}
                />
            )}

            <Input
                id="uff-label"
                label="Etiqueta"
                hint="Cómo se ve el campo para el usuario. Ej: Documento de identidad"
                error={form.formState.errors.label?.message}
                {...form.register('label')}
            />

            {/* Type: editable solo al crear; inmutable al editar (se muestra readonly). */}
            {isEdit ? (
                <div className="space-y-1">
                    <Label>Tipo (no editable)</Label>
                    <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                        {field.type === 'select' ? 'Lista de opciones' : 'Texto'}
                    </p>
                </div>
            ) : (
                <div className="space-y-1">
                    <div className="flex items-center gap-1">
                        <Label>Tipo</Label>
                        <HelpTooltip content="text = texto libre; select = lista de opciones predefinidas." />
                    </div>
                    <Select
                        value={type}
                        onChange={(value) => {
                            form.setValue('type', value as FormValues['type'], {
                                shouldValidate: true,
                            });
                        }}
                        options={[
                            { value: 'text', label: 'Texto' },
                            { value: 'select', label: 'Lista de opciones' },
                        ]}
                    />
                </div>
            )}

            {/* Editor de opciones: solo cuando el tipo es lista. */}
            {type === 'select' && (
                <div className="space-y-2">
                    <Label>Opciones</Label>
                    <div className="space-y-2">
                        {options.map((_, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    aria-label={`Opción ${String(idx + 1)}`}
                                    placeholder={`Opción ${String(idx + 1)}`}
                                    {...form.register(`options.${String(idx)}.value` as const)}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar opción ${String(idx + 1)}`}
                                    onClick={() => {
                                        removeOption(idx);
                                    }}
                                >
                                    <Trash2 className="size-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                    {optionsError && <p className="text-xs text-destructive">{optionsError}</p>}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        leftIcon={<Plus className="size-4" />}
                        onClick={addOption}
                    >
                        Agregar opción
                    </Button>
                </div>
            )}

            <div className="flex items-start gap-3">
                <Checkbox
                    id="uff-required"
                    checked={requiredChecked}
                    onCheckedChange={(checked) => {
                        form.setValue('required', checked === true, { shouldDirty: true });
                    }}
                    className="mt-0.5"
                />
                <div className="space-y-0.5">
                    <Label htmlFor="uff-required" className="cursor-pointer">
                        Obligatorio
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        El usuario debe completarlo al crear/editar su cuenta.
                    </p>
                </div>
            </div>

            <div className="flex items-start gap-3">
                <Checkbox
                    id="uff-inject"
                    checked={injectChecked}
                    onCheckedChange={(checked) => {
                        form.setValue('injectToToken', checked === true, { shouldDirty: true });
                    }}
                    className="mt-0.5"
                />
                <div className="space-y-0.5">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="uff-inject" className="cursor-pointer">
                            Incluir en la sesión
                        </Label>
                        <HelpTooltip content="Si se activa, el valor del campo se incluye en la sesión del usuario." />
                    </div>
                </div>
            </div>

            {/* `enabled` solo se controla al editar (al crear nace habilitado server-side). */}
            {isEdit && (
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="uff-enabled"
                        checked={enabledChecked}
                        onCheckedChange={(checked) => {
                            form.setValue('enabled', checked === true, { shouldDirty: true });
                        }}
                        className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                        <Label htmlFor="uff-enabled" className="cursor-pointer">
                            Habilitado
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            Si lo deshabilitas, deja de ofrecerse en los formularios de usuario sin
                            perder el histórico de valores.
                        </p>
                    </div>
                </div>
            )}

            <Button type="submit" disabled={mut.isPending} isLoading={mut.isPending}>
                {isEdit ? 'Guardar' : 'Crear campo'}
            </Button>
        </form>
    );
}
